/**
 * ModerationCaseCardHeader Component
 *
 * Header for moderation case cards with content type indicators and metadata.
 * Shows Badge components for available content types with counts and displays
 * metadata like segment name, username, user ID, and timestamp. Renders a "..." action
 * menu trigger button alongside the metadata.
 */

import type { ContentTypeInfo } from '@moderation/domain/ModerationCaseCard';
import {
  Icon,
  IconSize,
  Badge,
  BadgeSize,
  BadgeVariant,
  Text,
  DefaultUser,
  CalendarSolid,
  IconButton,
  IconButtonSize,
  MoreActions,
  ButtonType,
} from '@soteria/design-system-ve';

import * as styles from './ModerationCaseCard.css';

export interface ModerationCaseCardHeaderProps {
  /**
   * Array of available content types with their counts
   */
  contentTypes: ContentTypeInfo[];

  /**
   * Segment name (optional)
   */
  segmentName?: string | null;

  /**
   * Username (optional)
   */
  username?: string | null;

  /**
   * User/Subject ID (optional)
   */
  userId?: string | null;

  /**
   * Formatted timestamp string (optional)
   */
  timestamp?: string | null;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether to show the action menu trigger button ("...")
   * @default false
   */
  showActionMenu?: boolean;

  /**
   * ID for the action menu trigger button (links to Menu via triggerId for accessibility)
   */
  actionMenuTriggerId?: string;

  /**
   * Handler when action menu button is clicked
   */
  onActionMenuToggle?: () => void;
}

/**
 * Header component for ModerationCaseCard
 *
 * @example
 * ```tsx
 * <ModerationCaseCardHeader
 *   contentTypes={[
 *     { type: 'text', count: 3 },
 *     { type: 'image', count: 4 }
 *   ]}
 *   segmentName="Screenshots"
 *   username="user123"
 *   userId="abc12345"
 *   timestamp="1/21/2026"
 *   showActionMenu
 *   actionMenuTriggerId="moderation-action-menu-trigger"
 *   onActionMenuToggle={() => setIsActionMenuOpen(prev => !prev)}
 * />
 * ```
 */
// eslint-disable-next-line complexity
export function ModerationCaseCardHeader({
  contentTypes,
  segmentName,
  username,
  userId,
  timestamp,
  className,
  showActionMenu = false,
  actionMenuTriggerId,
  onActionMenuToggle,
}: ModerationCaseCardHeaderProps) {
  return (
    <div className={`${styles.headerContainer} ${className || ''}`.trim()}>
      {/* Content Type Badges */}
      {contentTypes.length > 0 && (
        <div className={styles.contentTypeBadges}>
          {contentTypes.map(({ type, count }) => (
            <Badge key={type} size={BadgeSize.Small} text={count > 1 ? `${type.toUpperCase()} (${count})` : type.toUpperCase()} />
          ))}
        </div>
      )}

      {/* Metadata Row */}
      <div className={styles.metadataRow}>
        {/* Segment Name Badge */}
        {segmentName && <Badge variant={BadgeVariant.Default}>{segmentName}</Badge>}

        {/* Username */}
        {username && (
          <div className={styles.metadataItem}>
            <Icon icon={DefaultUser} iconSize={IconSize.XSmall} />
            <Text>{username}</Text>
            {userId && userId !== username && <Text>#{userId}</Text>}
          </div>
        )}

        {/* Timestamp */}
        {timestamp && (
          <div className={styles.metadataItem}>
            <Icon icon={CalendarSolid} iconSize={IconSize.XSmall} />
            <Text>{timestamp}</Text>
          </div>
        )}

        {/* Action Menu Trigger Button ("...") */}
        {showActionMenu && onActionMenuToggle && (
          <IconButton
            aria-label="More actions"
            buttonSize={IconButtonSize.Small}
            buttonType={ButtonType.Secondary}
            icon={MoreActions}
            iconSize={IconSize.Small}
            id={actionMenuTriggerId}
            title="More actions"
            onClick={onActionMenuToggle}
          />
        )}
      </div>
    </div>
  );
}
